// See [Barrel File Removal FAQ](https://hello.atlassian.net/wiki/x/KJT2aAE)
/* eslint-disable no-barrel-files/no-barrel-files */
import {
	Scope as ConfluenceScope,
	type PageBlogResponse,
	type SupportedScopedResponses,
} from './confluence/types';
import { type IssueResponse, Scope as JiraScope, type SupportedResponseTypes } from './jira/types';
import { type LoomItemResponse, Scope as LoomScope } from './loom/types';
import { type AggregatorTownsquareResponse, TownsquareScope } from './townsquare/types';

export interface ModelParam {
	'@type': string;
	[value: string]: string | number;
}

export interface SearchContext {
	sessionId: string;
	referrerId: string | null;
}

export interface ABTest {
	abTestId: string;
	controlId: string;
	experimentId: string;
}

export interface ExperimentResponse<T> {
	scopes: {
		id: T;
		abTest: ABTest;
	}[];
}

export type CommonFilterType = 'site';

/**
 * All filters must implement this interface.
 */
export interface FilterInterface {
	'@type': string;
}

export enum Products {
	jira = 'JIRA',
	confluence = 'CONFLUENCE',
	townsquare = 'TOWNSQUARE',
	trello = 'TRELLO',
	bitbucket = 'BITBUCKET',
	other = 'OTHER',
}

export interface Site {
	product: Products;
	avatarUrl: string;
	cloudId: string;
	collabGraphSessionId?: string;
	siteName: string;
	siteUrl: string;
}

export interface SearchAnalyticParams {
	/**
	 * Used to tag analytics so it can be differentiated with other consumers of search,
	 * Example: 'media-editor' or 'portfolio-search'
	 */
	identifier: string;
	/**
	 * This is used to group together multiple searches.
	 * If used this should be a UUID.
	 *
	 * Note that the search client does not maintain any logic as to when this is invalidated it only guarantees that this is passed
	 * to the backend.
	 */
	sessionId?: string;
}

export type AllSupportedResponseAndScopes =
	| SupportedScopedResponses
	| SupportedResponseTypes
	| AggregatorTownsquareResponse
	| LoomItemResponse;

// oxlint-disable-next-line eslint/no-redeclare
export type Scope = AllSupportedResponseAndScopes['id'];
export type { PageBlogResponse, IssueResponse };

export const Scope: {
	Loom: LoomScope.Loom;
	TownsquareProject: TownsquareScope.TownsquareProject;
	TownsquareGoal: TownsquareScope.TownsquareGoal;
	TownsquareHelpLink: TownsquareScope.TownsquareHelpLink;
	TownsquareQuestion: TownsquareScope.TownsquareQuestion;
	TownsquareTag: TownsquareScope.TownsquareTag;
	TownsquareLearning: TownsquareScope.TownsquareLearning;
	People: TownsquareScope.People;
	Teams: TownsquareScope.Teams;
	JiraIssue: JiraScope.JiraIssue;
	JiraPlan: JiraScope.JiraPlan;
	JiraDashboard: JiraScope.JiraDashboard;
	JiraIssueDashboardBoardProjectFilter: JiraScope.JiraIssueDashboardBoardProjectFilter;
	JiraBoardProjectFilter: JiraScope.JiraBoardProjectFilter;
	JiraBoardProjectFilterPlan: JiraScope.JiraBoardProjectFilterPlan;
	JiraProject: JiraScope.JiraProject;
	ConfluencePageBlogAttachment: ConfluenceScope.ConfluencePageBlogAttachment;
	ConfluencePageBlog: ConfluenceScope.ConfluencePageBlog;
	ConfluenceExperimentsPageBlogAttachment: ConfluenceScope.ConfluenceExperimentsPageBlogAttachment;
	ConfluenceSpace: ConfluenceScope.ConfluenceSpace;
	UserConfluence: ConfluenceScope.UserConfluence;
	ConfluencePageBlogWhiteboard: ConfluenceScope.ConfluencePageBlogWhiteboard;
	ConfluenceContentType: ConfluenceScope.ConfluenceContentType;
} = {
	...ConfluenceScope,
	...JiraScope,
	...TownsquareScope,
	...LoomScope,
};
